import React, { useState, useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import "./TypeAndDisplay.css";
import switchImage from "./data/images/switch.png";

// Greek Unicode range: U+0370–U+03FF (Greek and Coptic) + tonos variants
const GREEK_CHAR_REGEX = /[\u0370-\u03FF\u1F00-\u1FFF]/;

const LATIN_ONLY_MESSAGE =
  "Μόνο λατινικοί χαρακτήρες επιτρέπονται λόγω φωνητικών κανόνων";

// Single-letter Latin mapping (phonetic table)
const LATIN_TO_HEBREW = {
  A: "א",
  B: "ב",
  C: "ק",
  D: "ד",
  E: "א",
  F: "פ",
  G: "ג",
  H: "ה",
  I: "י",
  J: "ג׳",
  K: "ק",
  L: "ל",
  M: "מ",
  N: "נ",
  O: "ו",
  P: "פ",
  Q: "ק",
  R: "ר",
  S: "ס",
  T: "ת",
  U: "ו",
  V: "ו",
  W: "וו",
  X: "קס",
  Y: "י",
  Z: "ז",
};

// Multi-letter sequences (order matters: longer patterns first)
const SPECIAL_COMBINATIONS = [
  { pattern: /CH/gi, replacement: "ח" },
  { pattern: /SH/gi, replacement: "ש" },
  { pattern: /TH/gi, replacement: "ת" },
  { pattern: /TZ/gi, replacement: "צ" },
  { pattern: /TS/gi, replacement: "צ" },
  { pattern: /PH/gi, replacement: "פ" },
  { pattern: /KH/gi, replacement: "ח" },
  { pattern: /OU/gi, replacement: "ו" },
  { pattern: /AE/gi, replacement: "א" },
];

/**
 * Greek phonetic mapping - preserved for future word-list processing.
 * Not used when input is Latin-only.
 */
// eslint-disable-next-line no-unused-vars -- Reserved for future word-list mode
const GREEK_PHONETIC_MAP = {
  Α: "א",
  Β: "ב",
  Γ: "ג",
  Δ: "ד",
  Ε: "ע",
  Ζ: "ז",
  Η: "ח",
  Θ: "ט",
  Ι: "י",
  Κ: "כ",
  Λ: "ל",
  Μ: "מ",
  Ν: "נ",
  Ξ: "קס",
  Ο: "ע",
  Π: "פ",
  Ρ: "ר",
  Σ: "ס",
  Τ: "ת",
  Υ: "ו",
  Φ: "פ",
  Χ: "כ",
  Ψ: "פס",
  Ω: "ע",
  Ά: "א",
  Έ: "ע",
  Ή: "ח",
  Ί: "י",
  Ό: "ע",
  Ύ: "ו",
  Ώ: "ע",
};

/** Greek clusters (μπ→ב, γκ→ג, ντ→ד) - for future word-list mode */
// eslint-disable-next-line no-unused-vars -- Reserved for future word-list mode
const GREEK_CLUSTERS = [
  { pattern: /ΜΠ/gi, replacement: "ב" },
  { pattern: /ΓΚ/gi, replacement: "ג" },
  { pattern: /ΝΤ/gi, replacement: "ד" },
  { pattern: /ΓΓ/gi, replacement: "נג" },
];

function TypeAndDisplay() {
  const divRef = useRef(null);
  const [mode, setMode] = useState("words");
  const [inputValue, setInputValue] = useState("");
  const [originalWords, setOriginalWords] = useState([]);
  const [selectedType, setSelectedType] = useState(1);
  const [hoveredLetterIndex, setHoveredLetterIndex] = useState(null);
  const [hoveredWordIndex, setHoveredWordIndex] = useState(null);
  const [solitreoImages, setSolitreoImages] = useState({});
  const [latinOnlyMessage, setLatinOnlyMessage] = useState(false);
  const [filteredInput, setFilteredInput] = useState("");

  // Load Solitreo images on component mount
  useEffect(() => {
    const hebrewLetters = {
      א: "alef",
      ב: "bet",
      ג: "gimel",
      ד: "dalet",
      ה: "he",
      ו: "waw",
      ז: "zayin",
      ח: "het",
      ט: "tet",
      י: "yud",
      כ: "kaf",
      ך: "kaf_sofit",
      ל: "lamed",
      מ: "mem",
      ם: "mem_sofit",
      נ: "nun",
      ן: "nun_sofit",
      ס: "samek",
      ע: "ayin",
      פ: "pe",
      ף: "pe_sofit",
      צ: "tsadik",
      ץ: "tsadik_sofit",
      ק: "quf",
      ר: "resh",
      ש: "shin",
      ת: "tav",
    };

    // Create mapping of Hebrew letters to their image paths
    Object.entries(hebrewLetters).forEach(([letter, name]) => {
      // Check if image exists before adding to mapping
      const img = new Image();
      const imagePath = `/images/Sol_${name}.png`;
      img.src = imagePath;
      img.onload = () => {
        setSolitreoImages((prevImages) => ({
          ...prevImages,
          [letter]: imagePath,
        }));
      };
      img.onerror = () => {
        console.warn(`Image not found: ${imagePath}`);
      };
    });
  }, []);

  const handleTypeClick = (index) => {
    setSelectedType(index);
  };

  const applySpecialCombinations = (text) => {
    let result = text;
    SPECIAL_COMBINATIONS.forEach(({ pattern, replacement }) => {
      result = result.replace(pattern, replacement);
    });
    return result;
  };

  const stripGreekCharacters = (text) => {
    return text.replace(GREEK_CHAR_REGEX, "");
  };

  const containsGreek = (text) => GREEK_CHAR_REGEX.test(text);

  // Helper function to convert regular Hebrew letters to sofit (final) form when at end of words
  const addSofitLetters = (text) => {
    const wordList = text.split(" ");
    return wordList
      .map((word) => {
        if (word.length === 0) return word;

        const lastChar = word[word.length - 1];
        let newWord = word;

        // Change last character to sofit form if applicable
        if (lastChar === "כ") newWord = word.slice(0, -1) + "ך";
        else if (lastChar === "מ") newWord = word.slice(0, -1) + "ם";
        else if (lastChar === "נ") newWord = word.slice(0, -1) + "ן";
        else if (lastChar === "פ") newWord = word.slice(0, -1) + "ף";
        else if (lastChar === "צ") newWord = word.slice(0, -1) + "ץ";

        return newWord;
      })
      .join(" ");
  };

  const handleInputChange = (event) => {
    const rawValue = event.target.value;
    const hasGreek = containsGreek(rawValue);

    if (hasGreek) {
      setLatinOnlyMessage(true);
      const latinOnly = stripGreekCharacters(rawValue);
      setFilteredInput(latinOnly);
    } else {
      setLatinOnlyMessage(false);
      setFilteredInput(rawValue);
    }

    let text = (hasGreek ? stripGreekCharacters(rawValue) : rawValue)
      .toUpperCase();
    text = applySpecialCombinations(text);

    setOriginalWords(text.split(" "));
    text = text
      .split("")
      .map((char) => LATIN_TO_HEBREW[char] ?? char)
      .join("");

    text = addSofitLetters(text);
    setInputValue(text);
  };

  const exportAsImage = async () => {
    const canvas = await html2canvas(divRef.current); // Capture the div content
    const image = canvas.toDataURL("image/png"); // Convert it to an image format (PNG)
    const link = document.createElement("a"); // Create an <a> element
    link.href = image; // Set the image as the href
    link.download = "transcribed_text.png"; // Set the default download name
    document.body.appendChild(link); // Append to the body temporarily
    link.click(); // Trigger the download
    document.body.removeChild(link); // Remove the link element
  };

  const shareImage = async () => {
    try {
      const canvas = await html2canvas(divRef.current);
      canvas.toBlob(async (blob) => {
        const file = new File([blob], "shared-image.png", {
          type: "image/png",
        });
        if (navigator.share) {
          try {
            await navigator.share({
              files: [file],
              title: "Shared Image",
              text: "Check out this image!",
            });
            console.log("Image shared successfully");
          } catch (error) {
            console.error("Error sharing the image:", error);
            alert("Error sharing the image. Please try again.");
          }
        } else {
          alert("Web Share API is not available in your browser.");
        }
      }, "image/png");
    } catch (error) {
      console.error("Failed to capture the image", error);
      alert("Failed to capture the image. Please try again.");
    }
  };

  const toggleMode = () => {
    // Reset hover states when changing modes
    setHoveredWordIndex(null);
    setHoveredLetterIndex(null);
    setMode((prevMode) => (prevMode === "words" ? "letters" : "words"));
  };

  return (
    <div className="type-and-display">
      <h1 id="title">
        Μεταγραφή Λατινικών Χαρακτήρων σε Εβραϊκούς
      </h1>
      <div className="transcibe-area">
        <div className="input-box">
          <textarea
            value={filteredInput}
            onChange={handleInputChange}
            placeholder="Πληκτρολόγησε σε Λατινικούς χαρακτήρες..."
            aria-label="Εισαγωγή κειμένου για μεταγραφή"
          />
          {latinOnlyMessage && (
            <p className="latin-only-message" role="alert">
              {LATIN_ONLY_MESSAGE}
            </p>
          )}
        </div>
        <div className="img-container">
          <img src={switchImage} alt="" />
        </div>
        <div className="transcribed">
          <div className="types-container">
            <div className="types desktop-types">
              <div
                className={`type ${selectedType === 0 ? "selected" : ""}`}
                onClick={() => handleTypeClick(0)}
              >
                Solitreo γραφή
              </div>
              <div
                className={`type ${selectedType === 1 ? "selected" : ""}`}
                onClick={() => handleTypeClick(1)}
              >
                Τετράγωνη γραφή
              </div>
              {/* <div
                className={`type ${selectedType === 2 ? "selected" : ""}`}
                onClick={() => handleTypeClick(2)}
              >
                Rashi γραφή
              </div> */}
            </div>
            <div className="mobile-types">
              <select
                value={selectedType}
                onChange={(e) => handleTypeClick(Number(e.target.value))}
                className="mobile-type-select"
              >
                <option value={0}>Solitreo γραφή</option>
                <option value={1}>Τετράγωνη γραφή</option>
              </select>
            </div>
          </div>
          <div
            ref={divRef}
            className={`hebrew-text ${selectedType === 2 ? "rashi" : ""}`}
          >
            {mode === "words" &&
              inputValue.split(" ").map((word, index) => {
                const isHovered = hoveredWordIndex === index;

                return (
                  <span
                    key={index}
                    className="word-div"
                    onMouseEnter={() => setHoveredWordIndex(index)}
                    onMouseLeave={() => setHoveredWordIndex(null)}
                  >
                    {isHovered
                      ? originalWords[index] || ""
                      : selectedType === 0
                      ? word.split("").map((letter, letterIdx) => (
                          <React.Fragment key={letterIdx}>
                            {solitreoImages[letter] ? (
                              <img
                                src={solitreoImages[letter]}
                                alt={letter}
                                className="solitreo-letter-img"
                                style={{
                                  height: "30px",
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                }}
                              />
                            ) : (
                              letter
                            )}
                          </React.Fragment>
                        ))
                      : word}{" "}
                  </span>
                );
              })}

            {mode === "letters" &&
              inputValue.split(" ").map((word, wordIndex) => (
                <span key={wordIndex} className="word-div">
                  {word.split("").map((letter, letterIndex) => {
                    // Generate a unique key for each letter based on its word and position
                    const uniqueLetterKey = `${wordIndex}-${letterIndex}`;
                    const isHovered = hoveredLetterIndex === uniqueLetterKey;
                    const originalLetter =
                      originalWords[wordIndex] &&
                      originalWords[wordIndex][letterIndex]
                        ? originalWords[wordIndex][letterIndex]
                        : "";

                    return (
                      <span
                        key={uniqueLetterKey}
                        className="letter-div"
                        onMouseEnter={() =>
                          setHoveredLetterIndex(uniqueLetterKey)
                        }
                        onMouseLeave={() => setHoveredLetterIndex(null)}
                      >
                        {isHovered ? (
                          originalLetter
                        ) : selectedType === 0 && solitreoImages[letter] ? (
                          <img
                            src={solitreoImages[letter]}
                            alt={letter}
                            className="solitreo-letter-img"
                            style={{
                              height: "30px",
                              display: "inline-block",
                              verticalAlign: "middle",
                            }}
                          />
                        ) : (
                          letter
                        )}
                      </span>
                    );
                  })}{" "}
                </span>
              ))}
          </div>
          <div className="svg-container">
            <button className="button-12" onClick={toggleMode}>{`${
              mode === "words" ? "Ανά γράμμα" : "Ανά λέξη"
            }`}</button>

            <svg
              className="svg"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{ cursor: "pointer" }}
              onClick={exportAsImage}
            >
              <path
                d="M17.5 12.5V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V12.5M14.1667 8.33333L10 12.5M10 12.5L5.83333 8.33333M10 12.5V2.5"
                stroke="#666666"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              className="svg"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{ cursor: "pointer" }}
              onClick={shareImage}
            >
              <path
                d="M7.15833 11.2583L12.85 14.575M12.8417 5.42501L7.15833 8.74167M17.5 4.16667C17.5 5.54738 16.3807 6.66667 15 6.66667C13.6193 6.66667 12.5 5.54738 12.5 4.16667C12.5 2.78596 13.6193 1.66667 15 1.66667C16.3807 1.66667 17.5 2.78596 17.5 4.16667ZM7.5 10C7.5 11.3807 6.38071 12.5 5 12.5C3.61929 12.5 2.5 11.3807 2.5 10C2.5 8.61929 3.61929 7.50001 5 7.50001C6.38071 7.50001 7.5 8.61929 7.5 10ZM17.5 15.8333C17.5 17.2141 16.3807 18.3333 15 18.3333C13.6193 18.3333 12.5 17.2141 12.5 15.8333C12.5 14.4526 13.6193 13.3333 15 13.3333C16.3807 13.3333 17.5 14.4526 17.5 15.8333Z"
                stroke="#666666"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TypeAndDisplay;
